// SoundProvider.tsx

import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import * as Tone from "tone";
import { pianoVoice } from "components/piano/MusicVoices";

// Define the shape of the SoundContext
interface SoundContextType {
  playChord: (notes: number[]) => Promise<void>;
  playNote: (midiNumber: number) => void;
  stopNote: (midiNumber: number) => void;
  isSamplerLoaded: boolean;
  sampler: Tone.Sampler | null;
  setVolume: React.Dispatch<React.SetStateAction<number>>;
  volume: number;
  isAudioPlaying: boolean;
  setIsAudioPlaying: React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the SoundContext with an initial undefined value
const SoundContext = createContext<SoundContextType | undefined>(undefined);

// Define the props for SoundProvider
interface SoundProviderProps {
  children: ReactNode;
}

export const SoundProvider: React.FC<SoundProviderProps> = ({ children }) => {
  const [sampler, setSampler] = useState<Tone.Sampler | null>(null);
  const [isSamplerLoaded, setIsSamplerLoaded] = useState<boolean>(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState<boolean>(false);
  const [volume, setVolume] = useState<number>(0); // Default volume in decibels (0 dB)

  useEffect(() => {
    // Create a Gain node with the initial volume
    const gainNode = new Tone.Gain(Tone.dbToGain(volume)).toDestination();

    // Initialize the Sampler with pianoVoice URLs and connect it to the Gain node
    const newSampler = new Tone.Sampler({
      urls: pianoVoice as Record<string, string>, // Ensure pianoVoice is correctly typed
      onload: () => {
        setIsSamplerLoaded(true);
      },
    }).connect(gainNode);

    setSampler(newSampler);

    // Cleanup function to dispose of the sampler and gain node when the component unmounts
    return () => {
      newSampler.dispose();
      gainNode.dispose();
    };
  }, []); // Empty dependency array ensures this runs once on mount

  // Update the volume when it changes
  useEffect(() => {
    if (sampler) {
      sampler.volume.value = volume; // Volume in decibels
    }
  }, [volume, sampler]);

  // Function to play a chord (array of MIDI note numbers)
  const playChord = useCallback(
    async (notes: number[]) => {
      if (!isSamplerLoaded || !sampler) {
        console.error("Sampler not loaded");
        return;
      }

      try {
        await Tone.start(); // Ensure Tone.js audio context is started
        const now = Tone.now();
        notes.forEach((note) => {
          const noteName = Tone.Frequency(note, "midi").toNote();
          sampler.triggerAttackRelease(noteName, "2n", now);
        });
      } catch (error) {
        console.error("Error playing chord:", error);
      }
    },
    [isSamplerLoaded, sampler]
  );

  // Function to play an individual note (MIDI note number)
  const playNote = useCallback(
    (midiNumber: number) => {
      if (!isSamplerLoaded || !sampler) {
        console.error("Sampler not loaded");
        return;
      }
      const note = Tone.Frequency(midiNumber, "midi").toNote();
      sampler.triggerAttack(note);
    },
    [isSamplerLoaded, sampler]
  );

  // Function to stop an individual note (MIDI note number)
  const stopNote = useCallback(
    (midiNumber: number) => {
      if (!isSamplerLoaded || !sampler) {
        console.error("Sampler not loaded");
        return;
      }
      const note = Tone.Frequency(midiNumber, "midi").toNote();
      sampler.triggerRelease(note);
    },
    [isSamplerLoaded, sampler]
  );

  // Define the context value
  const value: SoundContextType = {
    playChord,
    playNote,
    stopNote,
    isSamplerLoaded,
    sampler,
    setVolume,
    volume,
    isAudioPlaying,
    setIsAudioPlaying,
  };

  return (
    <SoundContext.Provider value={value}>{children}</SoundContext.Provider>
  );
};

// Custom hook to use the SoundContext
export const useSound = (): SoundContextType => {
  const context = useContext(SoundContext);
  if (!context) {
    throw new Error("useSound must be used within a SoundProvider");
  }
  return context;
};
